import React from 'react';
import { about } from '../data/mock';
import { Leaf, Heart, Shield, Users, Award, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const APropos = () => {
  const values = [
    {
      icon: Leaf,
      title: 'Respect de l\'environnement',
      description: 'Nous sommes engagés dans une démarche éco-responsable pour préserver les espaces naturels qui nous entourent.'
    },
    {
      icon: Heart,
      title: 'Accueil chaleureux',
      description: 'Nous mettons un point d\'honneur à offrir un accueil personnalisé et convivial à tous nos visiteurs.'
    },
    {
      icon: Shield,
      title: 'Sécurité et professionnalisme',
      description: 'Nos équipes sont formées et diplômées pour garantir votre sécurité lors de chaque activité.'
    },
    {
      icon: Users,
      title: 'Partage et découverte',
      description: 'Nous aimons transmettre notre passion pour ce territoire d\'exception et ses richesses naturelles.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/terrasse.jpg')" }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">À propos de nous</h1>
          <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
            Plus de 20 ans d'expérience dans l'accueil et les activités de pleine nature
          </p>
        </div>
      </section>

      {/* Histoire */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8 text-gray-900">Qui sommes-nous ?</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            Nature Cévennes, à Meyrueis en Lozère, en plein cœur des Gorges du Tarn, vous propose différents services et prestations d'activités et sports de plein air avec "Fremyc", un camping base de loisir, agréé jeunesse et sport, "La Pègue" pour l'hébergement de groupe de jeunes de 6 à 17 ans et un gîte d'étape et de séjour "La Draille" à Meyrueis.          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">Nos valeurs</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 bg-[#afab53] rounded-lg flex items-center justify-center mb-6 mx-auto">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">{value.title}</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Agréments */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="/corniches.jpg" 
                alt="Nature Cévennes"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Certifications & Agréments</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Notre structure bénéficie de nombreux agréments et certifications qui garantissent la qualité de nos services.
              </p>
              <div className="space-y-4">
                {about.agréments.map((agrement, index) => (
                  <div key={index} className="flex items-start gap-4 bg-gray-50 rounded-lg p-4">
                    <div className="w-10 h-10 bg-[#afab53] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{agrement}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Territoire */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-gray-900 text-center">Notre territoire</h2>
          <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto leading-relaxed">
            {about.territoire.description}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {about.territoire.sitesProches.map((site, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-[#afab53] flex-shrink-0" />
                  <h3 className="font-semibold text-gray-900">{site}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#afab53] to-[#8a8742]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Rejoignez l'aventure !</h2>
          <p className="text-xl mb-10 text-white/90 leading-relaxed">
            Découvrez les Cévennes avec une équipe passionnée et professionnelle.
          </p>
          <Button size="lg" className="bg-white text-[#afab53] hover:bg-gray-100 text-lg px-8 py-6">
            Nous contacter
          </Button>
        </div>
      </section>
    </div>
  );
};

export default APropos;